import { useState } from 'react';
import classNames from 'classnames';
import { Body } from '@components/text';
import './Toggle.scss';

export interface ToggleOption {
  key: string;
  label: string;
}

export interface ToggleProps {
  /** Identifier for the element */
  id?: string;
  /** Array of ToggleOption to display */
  options: [ToggleOption, ToggleOption];
  /** Default value selected, if not provided first option is the default key */
  defaultKeyValue?: string;
  /** Style of the Toggle component. Default solid */
  style?: 'solid' | 'outlined';
  /** Size of the Toggle component. Default small */
  size?: 'large' | 'small';
  /** Additional classnames for the component */
  className?: string;
  /** Callback fired when the toggle changes */
  onChange?: (key: string) => void;
}

/**
 * Toggle component - `solid` style used in light theme, `outlined` style used in dark theme
 * @param id - Identifier for the element.
 * @param options - Array of ToggleOption to display.
 * @param defaultKeyValue - Default value selected, if not provided first option is the default key.
 * @param style - Style of the Toggle component. Default solid
 * @param size - Size of the Toggle component. Default small.
 * @param className - Additional classnames for the component.
 * @param onChange - Callback fired when the toggle changes.
 */
export const Toggle: React.FC<ToggleProps> = ({
  id,
  options,
  defaultKeyValue,
  style = 'solid',
  size = 'small',
  className,
  onChange,
  ...rest
}) => {
  const [selected, setSelected] = useState(defaultKeyValue || options[0].key);

  const handleSelect = (key: string) => {
    setSelected(key);
    if (onChange) onChange(key);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, key: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleSelect(key);
    }
  };

  return (
    <div
      id={id}
      role="radiogroup"
      className={classNames(
        'vds-toggle',
        `vds-toggle--${size}`,
        `vds-toggle--${style}`,
        {
          'vds-toggle--outlined--active-left': style === 'outlined' && selected === options[0].key,
          'vds-toggle--outlined--active-right': style === 'outlined' && selected === options[1].key,
        },
        className,
      )}>
      {options.map(({ key, label }, index) => (
        <div
          key={key}
          onClick={() => handleSelect(key)}
          onKeyDown={event => handleKeyDown(event, key)}
          className={classNames([`vds-toggle--${style}__option`], {
            'vds-toggle--solid__option--active': selected === key,
            'vds-toggle--solid__option__left': index === 0,
            'vds-toggle--solid__option__right': index === 1,
          })}
          tabIndex={0}
          role="radio"
          aria-checked={selected === key}
          {...rest}>
          <div className={`vds-toggle--${style}__label`}>
            <Body
              variant={style === 'outlined' && size === 'large' ? 'body-02' : 'caption'}
              color="primary"
              weight="semibold"
              size={size}
              className={selected === key ? 'toggle-active' : undefined}>
              {label}
            </Body>
          </div>
        </div>
      ))}
    </div>
  );
};
